/**
 * RecordObservationForm (P429.3).
 *
 * An integrity measure is judged only on what was observed, so the
 * screen that says "cannot be judged -- none is recorded" offers the
 * way to record one, beside it. Every field the API requires is asked
 * for here, including where the figure came from: a value without a
 * source is an assertion, and the API refuses it.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";

const SOURCE_KINDS = ["test", "drill", "incident"] as const;

export function RecordObservationForm({
  ibsId,
  metric,
  unit,
}: {
  ibsId: string;
  metric: string;
  unit: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [date, setDate] = useState("");
  const [kind, setKind] = useState<(typeof SOURCE_KINDS)[number]>("test");
  const [ref, setRef] = useState("");

  const record = useMutation({
    mutationFn: () =>
      api.post(`/v1/admin/twin/ibses/${ibsId}/tolerance-observations`, {
        metric,
        value,
        // A date picked on a form is a day; noon UTC keeps it on that day
        // in every timezone the bank reads it from.
        observed_at: `${date}T12:00:00Z`,
        source_kind: kind,
        source_ref: ref.trim(),
      }),
    onSuccess: () => {
      setOpen(false);
      setValue("");
      setDate("");
      setRef("");
      void qc.invalidateQueries({ queryKey: ["twin", "tolerance-metrics"] });
    },
  });

  const n = Number(value);
  const ready =
    value.trim() !== "" &&
    Number.isFinite(n) &&
    n >= 0 &&
    date !== "" &&
    ref.trim() !== "";

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`record-observation-${metric}`}
        onClick={() => setOpen(true)}
        className="mt-1 text-xs text-brand-brassDark hover:underline"
      >
        {t("toleranceMetrics.observation.open")}
      </button>
    );
  }
  return (
    <form
      data-testid={`observation-form-${metric}`}
      className="mt-1.5 flex flex-wrap items-end gap-2 text-xs"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) record.mutate();
      }}
    >
      <label className="flex flex-col">
        {t("toleranceMetrics.observation.value", { unit })}
        <input
          data-testid="observation-value"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-24 rounded border border-slate-300 px-1.5 py-1"
        />
      </label>
      <label className="flex flex-col">
        {t("toleranceMetrics.observation.date")}
        <input
          data-testid="observation-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded border border-slate-300 px-1.5 py-1"
        />
      </label>
      <label className="flex flex-col">
        {t("toleranceMetrics.observation.kind")}
        <select
          data-testid="observation-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as (typeof SOURCE_KINDS)[number])}
          className="rounded border border-slate-300 px-1.5 py-1"
        >
          {SOURCE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`toleranceMetrics.observation.kinds.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex grow flex-col">
        {t("toleranceMetrics.observation.source")}
        <input
          data-testid="observation-source"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder={t("toleranceMetrics.observation.sourcePlaceholder")}
          className="rounded border border-slate-300 px-1.5 py-1"
        />
      </label>
      <button
        type="submit"
        data-testid="observation-save"
        disabled={!ready || record.isPending}
        className="rounded bg-brand-brass px-2 py-1 text-brand-navy disabled:opacity-50"
      >
        {t("toleranceMetrics.observation.save")}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="px-1 py-1 text-slate-600 hover:underline"
      >
        {t("toleranceMetrics.observation.cancel")}
      </button>
      {record.isError && (
        <p className="w-full text-rose-700">
          {t("toleranceMetrics.observation.error")}
        </p>
      )}
    </form>
  );
}
